import React, { useEffect, useRef, useState } from "react";
import { Badge, Modal } from "react-bootstrap";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import HttpManager from "../../../../services/HttpManager";
import { PromoRequestCreated, PromoRequestRow } from "../../../../services/v2/promoApplications.service";
import { EnCaminoDetail } from "../../../../components/promos/tipos";
import BandaEnCamino, { BarraAvance, montoEC, S_EC } from "../../../../components/promos/bandaEnCamino";
import CambiarPromocion from "../../../../components/promos/cambiarPromocion";
import useCambioPromoAcceso from "../../../../components/promos/useCambioPromoAcceso";
import AccionesAlumno from "../../../../components/enrollment/accionesAlumno";
import TiraDeMeses from "../../../../components/acuerdos/tiraDeMeses";
import useEsTelefono from "../../../../utils/esTelefono";
import { fechaHoraAmPm } from "../../../../utils/fechaHoraAmPm";
import horarioBonito from "../../../../utils/horarioBonito";
import { cursoCorto } from "../../../../utils/cursoCorto";
import { tituloEstado } from "../../../../utils/tituloEstado";
import { errorText } from "../../managment/enrollmentRequests/helpers";
import { ORIGIN_LABEL, ORIGIN_SIN_COBRO, PendingRow, PromoApplicationDetail, SOURCE_SOLICITUD_PROMO, STATUS_ANULADA } from "./interfaces";
import { conPorcentaje, entregaDe, entregado, estadoAplicada, fecha, monto, nombreRegalo, queRegalo, regalosDe, tira } from "./formato";
import S from "./strings";

/*
	Detalle de Promociones en ventana, como el de Matrículas (Esteban, 2026-11-01): título con número y estado, los datos en un
	bloque, y abajo los botones de lo que se puede hacer con ella. Una ventana por tipo de fila: aplicada (o anulada), En Camino
	(o fallida), pendiente de aplicar y devuelta por Caja.
*/

const http = HttpManager.getInstance();

export const Dato: React.FC<{ t: string; children: React.ReactNode }> = ({ t, children }) => <div className="wpmDato"><small>{t}</small><div>{children}</div></div>;
export const Sub: React.FC<{ children: React.ReactNode }> = ({ children }) => <div className="wpmSub">{children}</div>;
export const Seccion: React.FC<{ t: React.ReactNode; children: React.ReactNode }> = ({ t, children }) => <div className="wpmSeccion"><h6>{t}</h6>{children}</div>;

export const Ventana: React.FC<{ titulo: React.ReactNode; onClose: () => void; busy?: boolean; pie?: React.ReactNode; children: React.ReactNode }> = ({ titulo, onClose, busy, pie, children }) => {
	const movil = useEsTelefono();
	return (
		<Modal className="waModulo wpmVentana" show onHide={busy ? undefined : onClose} size="lg" centered scrollable fullscreen="sm-down">
			<Modal.Header closeButton={!busy}>
				<Modal.Title style={{ fontSize: movil ? 15 : 18, display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>{titulo}</Modal.Title>
			</Modal.Header>
			<Modal.Body style={{ textAlign: "left", fontSize: 15, paddingLeft: movil ? 14 : 22, paddingRight: movil ? 14 : 22 }}>{children}</Modal.Body>
			{pie ? <Modal.Footer className="wpmPie">{pie}</Modal.Footer> : null}
		</Modal>
	);
};

const Cargando: React.FC = () => <div className="loader" />;

/** El curso de la persona y su horario, en dos renglones. */
export const Curso: React.FC<{ curso?: string | null; horario?: string | null }> = ({ curso, horario }) => (
	<>{cursoCorto(curso) || curso || "-"}{horario ? <Sub>{horarioBonito(horario) || horario}</Sub> : null}</>
);

// ------------------------------------------------------------------ aplicada / anulada

export const DetalleAplicada: React.FC<{ campusId: number; id: number; sym: string; onClose: () => void; onChanged: () => void }> = ({ campusId, id, sym, onClose, onChanged }) => {
	const navigate = useNavigate();
	// «Ir a Entregar» solo para quien ve Control de Entregas (sección kits).
	const usuario: any = useSelector((state: any) => state.userProfile?.value);
	const veEntregas = !!usuario?.roleMap?.[usuario?.currentRole]?.permits?.kits || usuario?.currentRole === "superAdmin";
	const [d, setD] = useState<PromoApplicationDetail | null>(null);
	const [error, setError] = useState<string | null>(null);
	const [cambiando, setCambiando] = useState<boolean>(false);
	const [vuelta, setVuelta] = useState<number>(0);
	const acceso = useCambioPromoAcceso(campusId, d && d.statusId !== STATUS_ANULADA ? [d.enrollmentId] : []);

	useEffect(() => {
		let vigente = true;
		http.promoApplicationsService.detail(campusId, id).then((r) => { if (vigente) setD(r.data); }).catch((e) => { if (vigente) setError(errorText(e)); });
		return () => { vigente = false; };
	}, [campusId, id, vuelta]);

	const s = d?.currencySymbol || sym;
	const est = d ? estadoAplicada(d) : null;
	const regalos = d ? regalosDe(d) : [];
	const porEntregar = regalos.filter((g) => !entregado(g));
	const meses = tira(d?.months);
	const mesesRegalo = tira(d?.giftMonths);
	const rc = d?.receipt;
	const historia = d ? [
		...(d.agreementEvents || []).map((e) => ({ at: e.at || null, titulo: e.name || "Acuerdo", texto: [e.text, e.by].filter(Boolean).join(" · ") })),
		...(d.history || []).map((e) => ({ at: e.at || null, titulo: e.reasonName || "Auditoría", texto: e.text })),
	].sort((a, b) => String(a.at || "").localeCompare(String(b.at || ""))) : [];

	return (
		<>
			<Ventana onClose={onClose} titulo={<>
				<span>{S.detailTitle(id)}{d ? " · " + d.student.name : ""}</span>
				{est ? <Badge bg={est.bg} style={{ fontWeight: 500 }}>{est.texto}</Badge> : null}
				{d?.kindName ? <Badge bg="secondary" style={{ fontWeight: 500 }}>{d.kindName}</Badge> : null}
			</>} pie={d ? <>
				<AccionesAlumno studentId={d.student.id} campusId={campusId} alinear="inicio" />
				<span style={{ flex: 1 }} />
				{porEntregar.length && veEntregas ? <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => navigate("/kits?q=" + encodeURIComponent(d.student.identityCode || d.student.name))}>{S.goDeliver}</button> : null}
				{acceso.puede(d.enrollmentId) ? <button type="button" className="btn btn-primary btn-sm" onClick={() => setCambiando(true)}>{S.changePromo}</button> : null}
			</> : null}>
				{error ? <div className="alert alert-danger">{error}</div> : !d ? <Cargando /> : <>
					{d.statusId === STATUS_ANULADA ? (
						<div className="wpmAvisoOro">
							<b>{S.cancelledBy(d.cancelledBy?.name || "", d.cancelledAt ? fechaHoraAmPm(d.cancelledAt) : "")}</b>
							{d.cancelReason ? <div>{d.cancelReason}</div> : null}
						</div>
					) : null}
					<div className="wpmDatos">
						<Dato t="Promoción">{d.promoName}{d.replacedPromos ? <Sub>{S.replaced(d.replacedPromos)}</Sub> : null}</Dato>
						<Dato t="Matrícula"><Curso curso={d.courseName} horario={d.scheduleText} /></Dato>
						{d.giftEnrollmentId ? <Dato t="Curso Regalado">{d.giftStudent?.name || ""}<Sub>{cursoCorto(d.giftCourseName) || d.giftCourseName || ""}</Sub></Dato> : null}
						<Dato t="Cobrado">{monto(s, d.amountPaid)}<Sub>{d.receiptId ? ["Recibo #" + d.receiptId, rc?.method, rc?.reference ? "Ref. " + rc.reference : ""].filter(Boolean).join(" · ") : d.origin === ORIGIN_SIN_COBRO ? "Sin Cobro" : ""}</Sub></Dato>
						<Dato t="Regalado">{monto(s, d.giftValue)}<Sub>{queRegalo(d)}</Sub></Dato>
						<Dato t="Fecha de Pago">{fecha(rc?.paymentDate || d.paymentDate) || "-"}{rc?.cancelled ? <Sub>Recibo Anulado</Sub> : null}</Dato>
						<Dato t="Pidió">{d.requestedBy?.name || "-"}</Dato>
						<Dato t="Aplicó">{d.cashier?.name || "-"}{d.origin ? <Sub>{ORIGIN_LABEL[d.origin] || d.origin}</Sub> : null}</Dato>
						<Dato t="Asesoras">{(d.advisors || []).length ? (d.advisors || []).map((a, i) => <div key={"as" + i} className="wpmAsesora">{conPorcentaje(a)}</div>) : "-"}</Dato>
					</div>

					{meses.length ? <Seccion t="Mensualidades"><TiraDeMeses sym={s} meses={meses} /></Seccion> : null}
					{mesesRegalo.length ? <Seccion t="Mensualidades del Curso Regalado"><TiraDeMeses sym={s} meses={mesesRegalo} /></Seccion> : null}

					{regalos.length ? (
						<Seccion t={<>{regalos.length === 1 ? "Producto Regalado" : "Productos Regalados"}{[d.giftOptionLabel, d.kit?.invoiceId ? "Factura #" + d.kit.invoiceId : ""].filter(Boolean).length
							? <span className="wpmSub" style={{ display: "inline", marginLeft: 6 }}>{[d.giftOptionLabel, d.kit?.invoiceId ? "Factura #" + d.kit.invoiceId : ""].filter(Boolean).join(" · ")}</span> : null}</>}>
							<table className="table table-sm wpmTabla">
								<tbody>
									{regalos.map((g, i) => (
										<tr key={"rg" + (g.owedArticleId || i)}>
											<td>{nombreRegalo(g)}</td>
											<td className="wpmNum">{g.value != null ? monto(s, g.value) : ""}</td>
											<td style={{ width: "1%", whiteSpace: "nowrap" }}><Badge bg={entregado(g) ? "secondary" : "oro"} style={{ fontWeight: 500 }}>{entregaDe(g)}</Badge>
												{g.deliveredAt ? <Sub>{fechaHoraAmPm(g.deliveredAt)}</Sub> : null}</td>
										</tr>
									))}
								</tbody>
							</table>
						</Seccion>
					) : null}

					{historia.length ? (
						<Seccion t="Historial">
							{historia.map((e, i) => (
								<div key={"hx" + i} className="wpmEvento"><span className="wpmSub" style={{ display: "inline" }}>{e.at ? fechaHoraAmPm(e.at) : ""}</span> <b>{e.titulo}</b>{e.texto ? <Sub>{e.texto}</Sub> : null}</div>
							))}
						</Seccion>
					) : null}
				</>}
			</Ventana>
			{cambiando && d ? (
				<CambiarPromocion campusId={campusId} studentId={d.student.id} enrollmentId={d.enrollmentId} courseName={d.courseName || ""}
					onClose={() => setCambiando(false)} onChanged={() => { setCambiando(false); acceso.recargar(); setVuelta((v) => v + 1); onChanged(); }} />
			) : null}
		</>
	);
};

// ------------------------------------------------------------------ En Camino / fallida

export const DetalleEnCamino: React.FC<{ campusId: number; id: number; sym: string; vivas: boolean; onClose: () => void; onChanged: () => void }> = ({ campusId, id, sym, vivas, onClose, onChanged }) => {
	const [d, setD] = useState<EnCaminoDetail | null>(null);
	const [error, setError] = useState<string | null>(null);
	const [cancelando, setCancelando] = useState<boolean>(false);
	const [motivo, setMotivo] = useState<string>("");
	const [busy, setBusy] = useState<boolean>(false);

	useEffect(() => {
		let vigente = true;
		http.promoApplicationsService.enCaminoDetail(campusId, id).then((r) => { if (vigente) setD(r.data); }).catch((e) => { if (vigente) setError(errorText(e)); });
		return () => { vigente = false; };
	}, [campusId, id]);

	const s = d?.currencySymbol || sym;
	const puedeCancelar = !!d && vivas && (d.status === "porCobrar" || d.status === "enCamino") && (d.payments || []).length === 0 && Number(d.paid) < 0.005;
	const cancelar = async () => {
		if (!d || busy || motivo.trim().length < 10) return;
		try {
			setBusy(true); setError(null);
			await http.promoApplicationsService.enCaminoCancel(campusId, d.id, motivo.trim());
			onChanged(); onClose();
		} catch (e) {
			setError(errorText(e));
		} finally {
			setBusy(false);
		}
	};

	return (
		<Ventana onClose={onClose} busy={busy} titulo={<>
			<span>{S.ecTitle(id)}{d?.studentName ? " · " + d.studentName : ""}</span>
			{d ? <Badge bg={vivas ? "info" : "oro"} style={{ fontWeight: 500 }}>{d.statusName || S_EC.title}</Badge> : null}
		</>} pie={d ? (cancelando ? <>
			<input className="form-control form-control-sm" style={{ flex: "1 1 260px", minHeight: 36 }} autoFocus value={motivo} disabled={busy} placeholder={S.ecCancelReason} onChange={(e) => setMotivo(e.target.value)} />
			<button type="button" className="btn btn-outline-secondary btn-sm" disabled={busy} onClick={() => { setCancelando(false); setMotivo(""); }}>{S.cancel}</button>
			<button type="button" className="btn btn-primary btn-sm" disabled={busy || motivo.trim().length < 10} onClick={cancelar}>{busy ? S.sending : S.ecCancelConfirm}</button>
		</> : <>
			<AccionesAlumno studentId={d.studentId} campusId={campusId} alinear="inicio" />
			<span style={{ flex: 1 }} />
			{puedeCancelar ? <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => setCancelando(true)}>{S.ecCancel}</button> : null}
		</>) : null}>
			{error ? <div className="alert alert-danger">{error}</div> : null}
			{!d ? (error ? null : <Cargando />) : <>
				{vivas ? <BandaEnCamino e={d} sym={s} conNombre={false} /> : (
					<div className="wpmAvisoOro">
						<div>{S_EC.leva(s, d.paid, d.target).replace(/^./, (c) => c.toUpperCase())}</div>
						<BarraAvance e={d} />
						{d.endedReason ? <div style={{ marginTop: 4 }}>{d.endedReason}</div> : null}
					</div>
				)}
				<div className="wpmDatos">
					<Dato t="Promoción">{d.promoName}</Dato>
					<Dato t="Matrícula"><Curso curso={d.courseName} horario={d.scheduleText} /></Dato>
					{d.giftEnrollmentId ? <Dato t="Curso Regalado">{d.giftStudentName || ""}<Sub>{cursoCorto(d.giftCourseName) || d.giftCourseName || ""}</Sub></Dato> : null}
					<Dato t={S.ecMeta}>{montoEC(s, d.target)}</Dato>
					<Dato t={S.ecPaid}>{montoEC(s, d.paid)}<Sub>{S_EC.faltan(s, d.remaining).replace(/^./, (c) => c.toUpperCase())}</Sub></Dato>
					<Dato t={S.ecDue}>{fecha(d.dueDate) || "-"}{d.startDate ? <Sub>{S.ecStart} {fecha(d.startDate)}</Sub> : null}</Dato>
					{d.requestedBy || d.createdBy ? <Dato t={S.ecBy}>{(d.requestedBy || d.createdBy)!.name}</Dato> : null}
					<Dato t="Asesoras">{(d.advisors || []).length ? (d.advisors || []).map((a, i) => <div key={"as" + i} className="wpmAsesora">{conPorcentaje({ name: a.name, sharePercentage: a.sharePercentage })}</div>) : "-"}</Dato>
					{d.attempt && d.attempt > 1 ? <Dato t={S.ecAttempt}>{d.attempt}</Dato> : null}
				</div>
				{(d.payments || []).length ? (
					<Seccion t={S.ecPayments}>
						<table className="table table-sm wpmTabla">
							<tbody>
								{(d.payments || []).map((p, i) => (
									<tr key={"pg" + i}><td>Recibo #{p.receiptId}</td><td>{fecha(p.date)}</td><td>{p.method || ""}</td><td className="wpmNum">{montoEC(s, p.amount)}</td></tr>
								))}
							</tbody>
						</table>
					</Seccion>
				) : null}
				{(d.history || []).length ? (
					<Seccion t={S.ecHistory}>
						{(d.history || []).map((h, i) => (
							<div key={"hs" + i} className="wpmEvento"><span className="wpmSub" style={{ display: "inline" }}>{h.at ? fechaHoraAmPm(h.at) : ""}</span> {h.text}{h.by ? <Sub>{h.by}</Sub> : null}</div>
						))}
					</Seccion>
				) : null}
			</>}
		</Ventana>
	);
};

// ------------------------------------------------------------------ por aplicar

export const DetallePendiente: React.FC<{ campusId: number; r: PendingRow; sym: string; onClose: () => void }> = ({ campusId, r, sym, onClose }) => {
	const navigate = useNavigate();
	const solicitud = r.source === SOURCE_SOLICITUD_PROMO;
	const origen = solicitud ? S.chipRequest : (r.sourceName || tituloEstado(r.source) || "");
	return (
		<Ventana onClose={onClose} titulo={<>
			<span>{r.ticketId ? S.ticketTitle(r.ticketId) : S.pendingTitle}{" · " + (r.payer.name || S.noName)}</span>
			<Badge bg="secondary" style={{ fontWeight: 500 }}>{S.statusPending}</Badge>
		</>} pie={<>
			{r.payer.studentId ? <AccionesAlumno studentId={r.payer.studentId} campusId={campusId} alinear="inicio" /> : null}
			<span style={{ flex: 1 }} />
			{r.ticketId
				? <button type="button" className="btn btn-primary btn-sm" onClick={() => navigate("/paymentIntents?ticket=" + r.ticketId + "&plantel=" + campusId)}>{S.openInCaja}</button>
				: <span className="wpmSub">{r.goesTo ? S.goesTo(r.goesTo) : S.noTicket}</span>}
		</>}>
			<div className="wpmDatos">
				<Dato t="Promoción">{r.promoName}{r.kindName ? <Sub>{r.kindName}</Sub> : null}</Dato>
				<Dato t={r.receiver ? "Paga" : "Matrícula"}>{r.receiver ? r.payer.name : null}<Curso curso={r.payer.courseName} horario={r.payer.scheduleText} /></Dato>
				{r.receiver ? <Dato t="Recibe">{r.receiver.name}<Curso curso={r.receiver.courseName} horario={r.receiver.scheduleText} /></Dato> : null}
				<Dato t="Monto">{r.amount != null ? monto(sym, r.amount) : "-"}</Dato>
				<Dato t="Origen">{origen}{r.createdAt ? <Sub>{fechaHoraAmPm(r.createdAt)}</Sub> : null}</Dato>
				{r.requestedBy?.name ? <Dato t="Pidió">{r.requestedBy.name}</Dato> : null}
				<Dato t="Asesoras">{(r.advisors || []).length ? (r.advisors || []).map((a, i) => <div key={"as" + i} className="wpmAsesora">{conPorcentaje(a)}</div>) : "-"}</Dato>
			</div>
		</Ventana>
	);
};

// ------------------------------------------------------------------ devuelta por Caja

export const DetalleDevuelta: React.FC<{ campusId: number; r: PromoRequestRow; sym: string; puedeReenviar: boolean; onClose: () => void;
	onReenviar: (intentId: number, files: File[], notes: string) => Promise<PromoRequestCreated> }> = ({ campusId, r, sym, puedeReenviar, onClose, onReenviar }) => {
	const [files, setFiles] = useState<File[]>([]);
	const [nota, setNota] = useState<string>("");
	const [busy, setBusy] = useState<boolean>(false);
	const [error, setError] = useState<string | null>(null);
	const input = useRef<HTMLInputElement | null>(null);
	const mandar = async () => {
		if (!files.length || busy) return;
		try {
			setBusy(true); setError(null);
			await onReenviar(r.intentId, files, nota);
			onClose();
		} catch (e) {
			setError(errorText(e));
		} finally {
			setBusy(false);
		}
	};
	return (
		<Ventana onClose={onClose} busy={busy} titulo={<>
			<span>{S.ticketTitle(r.intentId)} · {r.student.name}</span>
			<Badge bg="oro" style={{ fontWeight: 500 }}>{S.returnedBadge}</Badge>
		</>} pie={<>
			<AccionesAlumno studentId={r.student.id} campusId={campusId} alinear="inicio" />
			<span style={{ flex: 1 }} />
			{puedeReenviar ? <button type="button" className="btn btn-primary btn-sm" disabled={busy || !files.length} onClick={mandar}>{busy ? S.sending : S.resendSend}</button> : null}
		</>}>
			<div className="wpmAvisoOro"><b>{S.reason}:</b> {r.returnedReason || "-"}{r.returnedAt ? <Sub>{fechaHoraAmPm(r.returnedAt)}</Sub> : null}</div>
			<div className="wpmDatos">
				<Dato t="Promoción">{r.promoName}</Dato>
				<Dato t="Matrícula"><Curso curso={r.course} horario={r.scheduleName} /></Dato>
				<Dato t="Monto">{r.amount != null ? monto(sym, r.amount) : "-"}</Dato>
				<Dato t="Pidió">{r.requestedBy?.name || "-"}</Dato>
			</div>
			{puedeReenviar ? (
				<Seccion t={S.resend}>
					<input ref={input} type="file" accept="image/*,application/pdf" multiple hidden disabled={busy}
						onChange={(e) => { setFiles((f) => [...f, ...Array.from(e.target.files || [])].slice(0, 5)); if (input.current) input.current.value = ""; }} />
					{files.map((f, i) => (
						<div key={"rf" + i} style={{ display: "flex", gap: 6, alignItems: "center", fontSize: 14, padding: "4px 0", borderBottom: "1px solid var(--wa-filo)" }}>
							<span style={{ flex: 1, minWidth: 0, overflowWrap: "anywhere" }}>{f.name}</span>
							<button type="button" className="btn btn-link btn-sm" aria-label="Quitar" disabled={busy} onClick={() => setFiles((l) => l.filter((_, j) => j !== i))}>×</button>
						</div>
					))}
					<div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 8 }}>
						{files.length < 5 ? <button type="button" className="btn btn-outline-secondary btn-sm" disabled={busy} onClick={() => input.current?.click()}>{files.length ? S.addVoucher : S.takePhoto}</button> : null}
						<input className="form-control form-control-sm" style={{ flex: "1 1 220px" }} placeholder={S.resendNote} value={nota} disabled={busy} onChange={(e) => setNota(e.target.value)} />
					</div>
					{error ? <div className="waOroTexto" style={{ fontSize: 14, marginTop: 6 }}>{error}</div> : null}
				</Seccion>
			) : null}
		</Ventana>
	);
};
